import {Component, Element, h, Listen, Prop, State} from "@stencil/core";
import {
    apiIdPressFilterMap,
    IPressAPIRequest,
    IPressAPIResponse,
    pageSizes,
    pressFilterAPIIDMap
} from "./dc-press-teaser.decl";
import {$t, downloadTranslations} from "./i18n";
import {IFilterChangeEvent, IFilterCheckGroup, IFilterGroup} from "../dc-filter/filter-types";
import {fetch} from "../../services/fetch";


const allItemsPerPageCount = 99999;
const dateFilterId = 'dateFilter';
const defaultRadioIDs = {
    dateFilter: 'date_all',
}

@Component({
    tag: 'dc-press-teaser',
    styleUrl: '../../assets/styles/style.scss',
    shadow: true,
})
export class DcPressTeaser {
    @Prop() apiHost = 'https://dc-ncj-portal.dev.dxf.bosch.tech';
    @Prop() country = 'xc';
    @Element() element: HTMLElement;
    @State() pressResponse: IPressAPIResponse = {press: [], stats: {}, totalResultCount: 0};
    @State() filterState?: Partial<IPressAPIRequest> = {};
    @State() itemsPerPage = 10;
    @Prop() mediaHost = 'https://dc-ncj-portal.dev.dxf.bosch.tech';
    @State() page = 0;
    @Prop() language = 'en';
    @State() loaded = false;
    private paginationEle?: HTMLDcPaginationElement;
    private taskList: Function[] = [];

    constructor() {
        (async () => {
            this.processURLParams();
            await Promise.all([
                downloadTranslations(this.mediaHost, this.language, this.country),
                this.updatePress(),
            ]);
            this.loaded = true;
        })();
    }

    @Listen('filterChange')
    filterChangeHandler(event: CustomEvent<IFilterChangeEvent>) {
        const v = event.detail.values;
        this.page = 0;
        this.filterState = {
            itemsPerPage: this.itemsPerPage,
            page: this.page,
            topic: v.topicFilter,
            contentType: v.contentTypeFilter,
            dateRange: v.dateFilter[0] ?? '',
        };

        this.updateURLParams();
        this.updatePress();
    }

    @Listen('filterAllClearPressed')
    filterClearHandler() {
        this.page = 0;
        this.filterState = {
            itemsPerPage: this.itemsPerPage,
            page: this.page,
            topic: [],
            contentType: [],
            dateRange: defaultRadioIDs.dateFilter,
        };

        this.updateURLParams();
        this.updatePress();
    }

    getURLParams(): Map<string, string[]> {
        const paramMap = new Map<string, string[]>();

        const search = window.location.search;

        if (search.length <= 2) {
            return paramMap;
        }

        search.slice(1).split('&').forEach(kvParam => {
            const [paramKey, paramValue] = kvParam.split('=');
            let mapValue = paramMap.get(paramKey);

            if (!mapValue) {
                mapValue = [];
                paramMap.set(apiIdPressFilterMap[paramKey], mapValue);
            }

            mapValue.push(paramValue);
        });

        return paramMap;
    }

    processURLParams() {
        const paramMap = this.getURLParams();
        if (paramMap.has('itemsPerPage')) {
            this.itemsPerPage = parseInt(paramMap.get('itemsPerPage')[0]);
            if (this.itemsPerPage == allItemsPerPageCount) {
                this.taskList.push(() => this.paginationEle?.showAll());
            }
        }

        if (paramMap.has('page')) {
            this.page = parseInt(paramMap.get('page')[0]);
        }

        if (paramMap.has('contentType')) {
            this.filterState.contentType = paramMap.get('contentType');
        }

        if (paramMap.has('dateRange')) {
            this.filterState.dateRange = paramMap.get('dateRange')[0]; // FIXME
        }

        if (paramMap.has('topic')) {
            this.filterState.topic = paramMap.get('topic');
        }
    }

    @Listen('pageChange')
    pageChangeHandler(event: CustomEvent<number>) {
        this.page = event.detail;
        this.updateURLParams();
        this.updatePress();
    }

    render() {
        if (!this.loaded) {
            return;
        }

        const noHorizontalPadding = { boxSizing: 'border-box' };
        let pressTeaser = this.pressResponse.press.map(event => (<div class="col-md-4 col-12" style={noHorizontalPadding}>
            <dc-press event={event} translateFn={$t} />
        </div >));
        const radios = [dateFilterId];
        const filters: IFilterGroup[] = Object.entries(this.pressResponse.stats).map(stat => {
            const id = stat[0];
            const isRadio = radios.includes(id);
            const itemState = this.filterState[apiIdPressFilterMap[id]];
            let items = Object.keys(stat[1]).map(option => {
                const checked = Array.isArray(itemState)
                    ? itemState.includes(option)
                    : itemState === option;
                return {
                    id: option,
                    checked,
                    label: $t(option),
                };
            });

            // Sort date filter so that "All" comes first and then the years are in desc order
            if (id == dateFilterId) {
                items = items.sort(
                    (a, b) =>
                        a.label < b.label
                            ? 1
                            : a.label == b.label
                                ? 0
                                : -1
                );
            }

            return {
                id,
                type: isRadio ? 'radio' : 'checkbox', // todo: make config
                name: $t(id),
                default: isRadio ? defaultRadioIDs[id] : undefined,
                open: true,
                displayShowMore: false,
                items,
            } as IFilterCheckGroup;
        });

        const lowerIndex = this.page * this.itemsPerPage + 1;
        const tmp = this.pressResponse.totalResultCount - (this.page + 1) * this.itemsPerPage
        const upperIndex = Math.min(this.pressResponse.totalResultCount, this.page * this.itemsPerPage + Math.max(this.pressResponse.totalResultCount % this.itemsPerPage, tmp % this.itemsPerPage, this.itemsPerPage <= this.pressResponse.totalResultCount ? this.itemsPerPage : 1));

        // execute tasks from list after rendering
        setTimeout(() => {
            for (const task of this.taskList) {
                task();
            }

            this.taskList.length = 0;
        });

        return (<div>
            <div class="row justify-content-end">
                {this.pressResponse.totalResultCount == 0 && <p class="col-9" style={noHorizontalPadding}>0 RESULTS</p>}
                {this.pressResponse.totalResultCount > 0 && <p class="col-9" style={noHorizontalPadding}>{$t('results', lowerIndex.toString(), upperIndex.toString(), this.pressResponse.totalResultCount.toString())}</p>}
            </div>

            <div class="row">
                <dc-filter
                    class="col-lg-3 col-md-3 col-sm-12"
                    filterCounts={this.pressResponse.stats}
                    filters={filters}
                    style={noHorizontalPadding}
                    translationFn={$t}
                />
                <div class="col-9 module-teaser-event" style={noHorizontalPadding}>
                    <div class="row">
                        {pressTeaser}
                    </div>
                </div>
            </div>

            <div class="row justify-content-end" style={noHorizontalPadding}>
                <div class="col-lg-9 col-md-9 col-sm-12">
                    <dc-pagination
                        ref={el => this.paginationEle = el}
                        currentPage={this.page}
                        pageCount={Math.ceil(this.pressResponse.totalResultCount / this.itemsPerPage)}
                        resultsPerPage={pageSizes}
                        scrollToRef={this.element}
                        totalResults={this.pressResponse.totalResultCount}
                        translationFn={$t}
                    />
                </div>
            </div>
        </div>);
    }

    @Listen('resultsPerPageChange')
    resultsPerPageChangeHandler(event: CustomEvent<string>) {
        this.itemsPerPage = event.detail == 'all' ? allItemsPerPageCount : parseInt(event.detail);
        this.page = 0;
        this.updateURLParams();
        this.updatePress();
    }

    async updatePress({itemsPerPage, page, topic, dateRange, contentType}: Partial<IPressAPIRequest> = {}) {
        const pressRequest = await fetch(new URL(`/customer-experience/dc-caas/press-overview-data?index=rb-dc-${this.country}-${this.language}`, this.apiHost).href, {
            body: JSON.stringify({
                dateRange: dateRange ?? this.filterState?.dateRange ?? '',
                contentType: contentType ?? this.filterState?.contentType ?? [],
                itemsPerPage: itemsPerPage ?? this.itemsPerPage,
                page: page ?? this.page,
                topic: topic ?? this.filterState?.topic ?? [],
            } as IPressAPIRequest),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            method: 'POST',
        });

        this.pressResponse = await pressRequest.json();
    }

    updateURLParams() {
        const paramMap = this.getURLParams();
        let searchStr = '';

        Object.entries(this.filterState).forEach(kv => {
            const key = kv[0];
            const value = kv[1];

            paramMap.set(key, Array.isArray(value)
                ? value
                : [value.toString()]
            )
        });

        paramMap.set('itemsPerPage', [this.itemsPerPage.toString()]);
        paramMap.set('page', [this.page.toString()]);

        paramMap.forEach((valueArr, key) => {
            for (const value of valueArr) {
                searchStr += `&${pressFilterAPIIDMap[key]}=${value}`;
            }
        });

        history.pushState(null, '', `${window.location.pathname}?${searchStr.slice(1)}`);
    }
}
